import type { PulseStory } from "@altair/shared/types";

/**
 * Maps a news story to canonical `/docs/*` deep links so each story passes
 * authority into the evergreen docs cluster (and gives crawlers + answer
 * engines a path from fresh news into reference content).
 *
 * SAFETY: the news-renderer cannot stat the blob-hosted docs at runtime, so we
 * only emit links to docs we KNOW exist via the allowlists below. Every target
 * here was verified to return 200. Update these sets when docs are added.
 * Source of truth: apps/web/public/docs/{denials,modifiers,payers}.
 *
 * CPT / HCPCS / ICD-10 are intentionally NOT linked: those doc clusters do not
 * exist (verified 404), so matching them would emit dead links.
 */

export interface RelatedLink {
  href: string;
  label: string;
}

/** CARC denial-code docs that exist under /docs/denials/. */
const CARC_DOCS = new Set([
  "co-4", "co-6", "co-11", "co-14", "co-15", "co-16", "co-18", "co-19",
  "co-21", "co-22", "co-23", "co-25", "co-26", "co-27", "co-29", "co-31",
  "co-32", "co-33", "co-39", "co-41", "co-42", "co-44", "co-45", "co-46",
  "co-47", "co-48", "co-50", "co-55", "co-97", "co-102", "co-103", "co-119",
  "co-167", "co-197", "co-204", "oa-23", "pr-1", "pr-2", "pr-3", "pr-96",
]);

/** Modifier docs that exist under /docs/modifiers/. */
const MODIFIER_DOCS = new Set([
  "22", "25", "26", "50", "51", "52", "53", "54", "55", "56", "59", "62",
  "63", "66", "76", "77", "91", "95",
]);

/**
 * Payer-chip name -> /docs/payers/<slug>. Only payers with a doc hub; agencies
 * like CMS are intentionally excluded (no payer doc). Keys match PAYER_COLORS
 * in components/news/payer-colors.ts.
 */
const PAYER_DOC_SLUGS: Record<string, string> = {
  UHC: "united-healthcare",
  Aetna: "aetna",
  BCBS: "bcbs",
  Cigna: "cigna",
  Humana: "humana",
  Anthem: "anthem-elevance",
  Molina: "molina",
  Kaiser: "kaiser",
  Medicaid: "medicaid",
  Medicare: "medicare-advantage",
};

// CARC codes: CO-4, PR 204, OA-16, etc. Modifiers: "modifier 25", "mod 59".
const CARC_RE = /\b(CO|PR|OA|PI|CR)[\s-]?(\d{1,3})\b/gi;
const MODIFIER_RE = /\b(?:modifier|mod)\s*[-–]?\s*(\d{2})\b/gi;

/**
 * Extract deduped, capped `/docs/*` links for a story. Payers first (the
 * highest-signal, structured source), then allowlisted CARC + modifier codes
 * found in the headline and summary.
 */
export function extractRelatedLinks(story: PulseStory, max = 5): RelatedLink[] {
  const out: RelatedLink[] = [];
  const seen = new Set<string>();
  const add = (href: string, label: string) => {
    if (seen.has(href)) return;
    seen.add(href);
    out.push({ href, label });
  };

  for (const payer of story.payers ?? []) {
    const slug = PAYER_DOC_SLUGS[payer];
    if (slug) add(`/docs/payers/${slug}`, `${payer} billing guide`);
  }

  const text = `${story.headline} ${story.summary}`;

  for (const m of text.matchAll(CARC_RE)) {
    const code = `${m[1].toLowerCase()}-${m[2]}`;
    if (CARC_DOCS.has(code)) {
      add(`/docs/denials/${code}`, `${m[1].toUpperCase()}-${m[2]} denial code`);
    }
  }

  for (const m of text.matchAll(MODIFIER_RE)) {
    const code = m[1];
    if (MODIFIER_DOCS.has(code)) add(`/docs/modifiers/${code}`, `Modifier ${code}`);
  }

  return out.slice(0, max);
}
